/**
 * ActionsDropdown Component
 *
 * Mobile dropdown fallback for bulk actions. At narrow viewports (≤600px),
 * the always-visible action buttons collapse into this dropdown menu.
 *
 * Hidden by default via CSS, becomes visible at narrow widths via container query.
 */

import { Dropdown, Button } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import type { BulkAction } from './BulkLifecycleToolbarContent'
import { renderIcon, showConfirmThenExecute } from './actionUtils'

/**
 * Props for ActionsDropdown component
 */
export interface ActionsDropdownProps {
  /** Number of selected rows */
  selectedCount: number
  /** All available bulk actions (lifecycle + record) */
  actions: BulkAction[]
  /** Called to execute a bulk action */
  onExecute: (actionKey: string) => Promise<void>
  /** Whether an operation is currently running */
  isExecuting?: boolean
  /** Entity label for messages (e.g., 'offerings', 'customers') */
  entityLabel?: string
  /**
   * Optional callback for 'activate' action with unified preview flow.
   * When provided, 'activate' clicks call this instead of showing Modal.confirm.
   * This enables the supersession-aware activation flow.
   */
  onActivateWithPreview?: () => void
}

/**
 * ActionsDropdown - Mobile dropdown for bulk actions
 *
 * Shows all bulk actions in a dropdown menu at narrow viewports.
 * Disabled items show why they can't be used. Enabled items show confirmation modal.
 *
 * @example
 * ```tsx
 * <ActionsDropdown
 *   selectedCount={5}
 *   actions={allBulkActions}
 *   onExecute={handleBulkExecute}
 *   entityLabel="offerings"
 * />
 * ```
 */
export function ActionsDropdown({
  selectedCount,
  actions,
  onExecute,
  isExecuting = false,
  entityLabel = 'items',
  onActivateWithPreview,
}: ActionsDropdownProps) {
  // Sort actions by order
  const sortedActions = [...actions].sort((a, b) => (a.order ?? 50) - (b.order ?? 50))

  // Build menu items
  const menuItems: MenuProps['items'] = sortedActions.map((action) => {
    const isDisabled = selectedCount === 0 || action.applicableCount === 0
    const showCount = selectedCount > 0 && action.applicableCount > 0

    // Build label with optional count
    let label = action.label
    if (showCount) {
      label = `${action.label} (${action.applicableCount})`
    }

    return {
      key: action.key,
      icon: renderIcon(action.icon),
      label,
      disabled: isDisabled || isExecuting,
      danger: action.danger,
    }
  })

  // Handle menu click with confirmation
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const action = sortedActions.find((a) => a.key === key)
    if (!action || action.applicableCount === 0) return

    // For 'activate' action with preview callback, use the unified flow
    if (action.key === 'activate' && onActivateWithPreview) {
      onActivateWithPreview()
      return
    }

    // Show confirmation modal using shared utility
    // This properly sequences modal close and progress modal open
    showConfirmThenExecute(action, entityLabel, () => onExecute(action.key))
  }

  // Count enabled actions for badge
  const enabledCount = sortedActions.filter(
    (a) => selectedCount > 0 && a.applicableCount > 0
  ).length

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click']}
      disabled={isExecuting}
    >
      <Button
        icon={<MoreOutlined />}
        className="actions-dropdown-btn"
      >
        Actions{enabledCount > 0 && ` (${enabledCount})`}
      </Button>
    </Dropdown>
  )
}
